import { zodResolver } from '@hookform/resolvers/zod';
import { adapter } from '@seek/tealium-adapter';
import { Actions, Alert, Box, Button, Stack, Text } from 'braid-design-system';
import { useState } from 'react';
// @ts-expect-error
import ReCAPTCHA from 'react-google-recaptcha';
import { FormProvider, useForm } from 'react-hook-form';

import { getAccountId, getSeekId } from 'src/utils/getAdvertiserId';
import { useConfig } from 'src/utils/hooks/useContext';

import { EnhancedFormValueSchema, type FormValues } from '../../EnquiryForm';

import { BusinessDetails } from './BusinessDetails';
import { IntegrationDetails } from './IntegrationDetails';
import { PersonalDetails } from './PersonalDetails';
import { Terms } from './Terms';

import * as styles from './Form.css';

const RECAPTCHA_KEY = '6LdwAKYUAAAAAGN9Svq7w2B0pOZWMrWbYD3GeQ8a';
const tealium = adapter();

interface FormProps {
  setSubmitted: (submitted: boolean) => void;
  resetForm: () => void;
  trackingProps: Record<string, unknown>;
  initialValues: FormValues;
}

export const Form = ({
  setSubmitted,
  resetForm,
  trackingProps,
  initialValues,
}: FormProps) => {
  const [invalidResponse, setInvalidResponse] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [recaptcha, setRecaptcha] = useState('');
  const { environment, locale } = useConfig();

  const methods = useForm({
    defaultValues: initialValues,
    resolver: zodResolver(EnhancedFormValueSchema),
    mode: 'onChange',
  });

  const postUrl =
    environment === 'production'
      ? 'https://wpt-contact-api.cloud.seek.com.au/integration'
      : 'https://wpt-contact-api.staging.cloud.seek.com.au/integration';

  const onSubmit = methods.handleSubmit(async (formValues: FormValues) => {
    if (!recaptcha && environment !== 'development') {
      return;
    }

    const accountNumber = getAccountId()
      ? getAccountId()?.replace(/^0+(?!\.|$)/, '')
      : '';
    const checkId = getSeekId();
    const seekId = checkId === '0' || checkId === null ? '' : checkId;
    setSubmitting(true);
    const {
      company,
      companyUrl,
      companyLocation,
      email,
      phone,
      firstName,
      lastName,
      ...restData
    } = formValues;
    const areCustomersPresent = (numberOfCustomers: number): string => {
      if (numberOfCustomers === 0) {
        return '';
      }
      return `Largest customers: ${restData.largestCustomers
        ?.map(
          (details?) => `${details?.seekId ?? '-'}:${details?.name ?? '-'}\n`,
        )
        .join('')}`;
    };
    const restDataSeekRspYesJson = `
    Customer segment: ${restData.customerSegment}
    # of SEEK customer: ${restData.numCustomers}
    ${areCustomersPresent(Number(restData?.numCustomers ?? 0))}
    Job ad volume per year: ${restData.jobAds}
    Currently integrated: ${restData.currentlyIntegrated}
    Solutions to support: ${restData.solutions.join(', ')}
    `;

    const restDataSeekRspNoJson = `
    Current recruitment software: ${restData.recruitmentSoftware}
    Bespoke integration?: ${restData.integrateToSeek}${
      restData.integrateToSeek === 'no'
        ? `\nReason for integration: ${restData.reasonForIntegration}`
        : ''
    }
    Currently integrated?: ${restData.currentlyIntegrated}
    Solutions to support: ${restData.solutions.join(', ')}
    `;

    const additionalInformationJson = `
    Website URL: ${companyUrl}
    Company Location: ${companyLocation}
    Job title: ${restData.jobTitle}
    Type of partner: ${restData.platformType}
    Type of partner (if Other): ${restData.platformTypeOther}

    Recruitment software provider?: ${restData.seekRsp} ${
      restData.seekRsp === 'yes'
        ? restDataSeekRspYesJson
        : restDataSeekRspNoJson
    }
    Why SEEK: ${restData.whySeek}
    Reviewed T&Cs: ${restData.reviewDocs}
    `;

    const data = {
      seekId,
      accountNumber,
      company,
      companyUrl,
      companyLocation,
      phone,
      email,
      firstName,
      lastName,
      subject: 'Integration request',
      additionalInformation: additionalInformationJson,
      siteCountry: locale,
      recaptcha,
    };

    if (environment === 'development') {
      console.log(data); // eslint-disable-line
      setSubmitted(true);
      return;
    }
    try {
      const response = await fetch(postUrl, {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.status === 200) {
        const analytics = {
          ...trackingProps,
          linkName: 'integration-request-form-submit',
        };
        tealium.link(analytics.linkName, analytics);
        resetForm();
        setInvalidResponse(false);
        setSubmitted(true);
      } else {
        throw new Error('API failed');
      }
    } catch (error) {
      setInvalidResponse(true);
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <form onSubmit={onSubmit}>
      <Stack space="large">
        <FormProvider {...methods}>
          <PersonalDetails />
          <BusinessDetails />
          <IntegrationDetails />
          <Terms />
        </FormProvider>
        <Stack space="medium">
          <Box className={styles.captchaContainer}>
            <ReCAPTCHA sitekey={RECAPTCHA_KEY} onChange={setRecaptcha} />
          </Box>
          {invalidResponse && (
            <Alert tone="critical">
              <Text id="api-failure-text">
                There was a problem submitting your request. Please try again.
              </Text>
            </Alert>
          )}
          <Actions>
            <Button tone="brandAccent" type="submit" loading={submitting}>
              {submitting ? 'Submitting' : 'Submit'}
            </Button>
          </Actions>
        </Stack>
      </Stack>
    </form>
  );
};
